import axios from 'axios';
import { PRIMARY_API_URL } from '../config/api';

const BASE_URL = PRIMARY_API_URL;
const LOCAL_STORAGE_KEY = 'lasmari_app_settings';

/**
 * Service for handling application settings
 * Prioritizes API database storage with localStorage as fallback
 */
class SettingsService {
  constructor() {
    this.defaultSettings = {
      pricing: {
        baseFee: 3,
        pricePerKm: 2,
        pricePerLuggage: 1,
        surchargePerPassenger: 0.2
      },
      defaultLocation: {
        name: "Lasmari Apraos",
        lat: 39.796276,
        lng: 19.884997
      },
      business: {
        name: "Lasmari Chauffeurs",
        address: "North Corfu",
        phone: "+30 2663098633",
        email: "info@lasmari.com",
        website: "https://lasmari.com"
      }
    };
    
    this.cachedSettings = null;
    this.lastFetchTime = 0;
    this.cacheExpiryMs = 15 * 60 * 1000; // Increased to 15 minutes
    this.apiEnabled = true; // Default to enabled since we now have a database
    this.isRequestInProgress = false; // Add flag to prevent simultaneous requests
  }
  
  /**
   * Get application settings prioritizing API with localStorage fallback
   */
  async getSettings() {
    // Use cache if it's still valid
    const now = Date.now();
    if (
      this.cachedSettings &&
      now - this.lastFetchTime < this.cacheExpiryMs
    ) {
      console.log('Using cached settings');
      return this.cachedSettings;
    }

    // Prevent multiple simultaneous requests
    if (this.isRequestInProgress) {
      console.log('Request already in progress, using last known settings or defaults');
      return this.cachedSettings || this.defaultSettings;
    }

    // Always try the API first now that we have a database
    if (this.apiEnabled) {
      try {
        this.isRequestInProgress = true;
        console.log('Fetching settings from API');
        const response = await axios.get(`${BASE_URL}/api/settings`);
        this.cachedSettings = response.data;
        this.lastFetchTime = now;
        
        // Store in localStorage as backup
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(response.data));
        this.isRequestInProgress = false;
        return this.cachedSettings;
      } catch (err) {
        console.warn('API error, falling back to localStorage:', err.message);
        this.apiEnabled = false; // Disable for this session if it fails
        this.isRequestInProgress = false;
      }
    }

    // If API fails, try localStorage
    const storedSettings = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (storedSettings) {
      try {
        const settings = JSON.parse(storedSettings);
        console.log('Using settings from localStorage fallback');
        this.cachedSettings = settings;
        this.lastFetchTime = now;
        return settings;
      } catch (err) {
        console.error('Error parsing stored settings:', err);
        // Continue with defaults if parsing fails
      }
    }

    // Last resort: use defaults
    console.log('Using default settings');
    this.cachedSettings = this.defaultSettings;
    this.lastFetchTime = now;
    // Save defaults to localStorage as backup
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this.defaultSettings));
    return this.defaultSettings;
  }

  /**
   * Reset API status to re-enable database attempts
   */
  resetApiStatus() {
    this.apiEnabled = true;
    console.log('API status reset - will attempt database save');
  }

  /**
   * Update application settings
   * Always tries API first, falls back to localStorage if needed
   */
  async updateSettings(settings) {
    // Prevent multiple simultaneous requests
    if (this.isRequestInProgress) {
      console.log('Another request is already in progress');
      return { success: false, error: 'Another request is in progress' };
    }
    
    this.isRequestInProgress = true;
    
    try {
      // Always update localStorage as backup
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(settings));
      
      // Update cache
      this.cachedSettings = settings;
      this.lastFetchTime = Date.now();
      
      // Try the API first (prioritize database storage)
      if (this.apiEnabled) {
        try {
          // Try to get auth token, but don't fail if missing since settings might not require auth
          const token = localStorage.getItem('admin_token') || localStorage.getItem('auth_token') || 'anonymous';
          
          console.log('Attempting to save settings to database...');
          
          const headers = {
            'Content-Type': 'application/json'
          };
          
          // Only add Authorization header if we have a real token
          if (token !== 'anonymous') {
            headers['Authorization'] = `Bearer ${token}`;
          }
          
          const response = await axios.put(
            `${BASE_URL}/api/settings`, 
            settings,
            { headers }
          );
          
          if (response.data.success) {
            console.log('✅ Settings saved to database successfully');
            return { success: true, settings };
          } else {
            console.warn('⚠️ API returned error:', response.data.error);
            return { success: true, settings, localOnly: true };
          }
        } catch (error) {
          console.warn('⚠️ Error saving to database:', error.message);
          // Don't disable API permanently, just for this request
          return { success: true, settings, localOnly: true };
        }
      }
      
      return { success: true, settings, localOnly: true };
    } finally {
      this.isRequestInProgress = false;
    }
  }
  
  /**
   * Reset API status to force a new attempt
   */
  resetApiStatus() {
    this.apiEnabled = true;
    this.cachedSettings = null;
    this.lastFetchTime = 0;
    this.isRequestInProgress = false;
  }
}

const settingsService = new SettingsService();
export default settingsService; 